import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getBreakReport, createBreakReportDownload } from '../../../../APICalling/APICalling';
import { useGlobalContext } from '../../../../Context/GlobalContext';
import Loader from '../../../../Components/Loader/Loader';
import { toast } from 'react-toastify';
import Echo from '../../../../Components/Sockect/Echo';
import ReportHeader from '../ReportHeader/ReportHeader';
import Placeholder from '../../../../Components/Placeholder/Placeholder';
import SearchNotFoundIcon from '../../../../Components/Icons/SearchNotFoundIcon';
import styles from './BreakReport.module.css';
import { getPeriodText, calculateReportDateRange, isReportRangeWithinMonths } from '../../../../Utils/ReportDateUtils';
import { clearSharedUserChannelEvent } from '../../../../Utils/clearSharedUserChannelEvent';
import { downloadFileWithAuth, getDownloadFilename } from '../../../../Utils/downloadFileWithAuth';

const BreakReport = ({ filterParams }) => {
	const { selectedOutlet, userAssignedOutlets } = useGlobalContext();
	const [breakData, setBreakData] = useState([]);
	const [loading, setLoading] = useState(false);
	const [loadingMore, setLoadingMore] = useState(false);
	const [downloadLoading, setDownloadLoading] = useState(false);
	const [outletName, setOutletName] = useState('');
	const [periodText, setPeriodText] = useState('');
	const [currentPage, setCurrentPage] = useState(1);
	const [hasMore, setHasMore] = useState(true);
	const [pagination, setPagination] = useState(null);
	const observer = useRef();
	const fetchBreakReportRef = useRef();

	useEffect(() => {
		if (filterParams && filterParams.date) {
			setCurrentPage(1);
			setBreakData([]);
			setHasMore(true);
			fetchBreakReport(1, true);
		}
	}, [filterParams]);

	// Get outlet name
	useEffect(() => {
		if (selectedOutlet && userAssignedOutlets?.length) {
			const outlet = userAssignedOutlets.find(o => o.id === selectedOutlet);
			setOutletName(outlet?.name || 'Unknown');
		}
	}, [selectedOutlet, userAssignedOutlets]);

	const fetchBreakReport = useCallback(async (page = 1, isInitialLoad = false) => {
		try {
			if (isInitialLoad) {
				setLoading(true);
			} else {
				setLoadingMore(true);
			}

			// Get user_id from localStorage
			const userId = localStorage.getItem('user_id');
			if (!userId) {
				toast.error('User ID not found');
				return;
			}

			// Calculate dates based on filter using utility
			const { startDate: startDateTime, endDate: endDateTime } = calculateReportDateRange(filterParams);

			const params = {
				daterange: `${startDateTime} / ${endDateTime}`,
				selected_date: startDateTime,
				outlet: filterParams.outlet || selectedOutlet,
				user_id: userId,
				page: page,
				archive: 1
			};

			if (filterParams?.staff !== undefined) {
				if (Array.isArray(filterParams.staff)) {
					params.therapist = filterParams.staff.join(',');
				} else {
					params.therapist = String(filterParams.staff || '').trim();
				}
			}
			// Reason filter: prefer status (current), fallback to status_id (older)
			const selectedStatus = filterParams?.status ?? filterParams?.status_id;
			if (selectedStatus !== undefined) {
				if (Array.isArray(selectedStatus)) {
					params.status = selectedStatus.join(',');
				} else {
					params.status = String(selectedStatus || '').trim();
				}
			}
			if (filterParams.archive === 1) {
				params.archive = 1;
			}

			if (isInitialLoad) {
				setPeriodText(getPeriodText(filterParams));
			}

			const response = await getBreakReport(params);

			if (response?.data?.success) {
				const dataList = response.data?.data?.list?.leave || [];
				const paginationInfo = response.data?.data?.list?.pagination;

				if (isInitialLoad) {
					setBreakData(dataList);
				} else {
					setBreakData(prev => [...prev, ...dataList]);
				}

				// Normalize pagination structure
				const normalizedPagination = paginationInfo ? {
					current_page: paginationInfo.currentPage,
					last_page: paginationInfo.lastPage,
					total: paginationInfo.count,
					per_page: paginationInfo.total
				} : null;

				setPagination(normalizedPagination);
				
				// Check if there are more pages
				if (normalizedPagination && normalizedPagination.current_page >= normalizedPagination.last_page) {
					setHasMore(false);
				}
			} else {
				if (isInitialLoad) {
					toast.error(response?.response?.data?.message || response?.data?.message || response?.message || 'Failed to fetch break report');
					setBreakData([]);
				}
			}
		} catch (error) {
			console.error('Error fetching break report:', error);
			if (isInitialLoad) {
				toast.error(error?.response?.data?.message || error?.message || 'An error occurred while fetching break report');
				setBreakData([]);
			}
		} finally {
			setLoading(false);
			setLoadingMore(false);
		}
	}, [filterParams, selectedOutlet]);

	// Store fetchBreakReport in ref for use in observer
	useEffect(() => {
		fetchBreakReportRef.current = fetchBreakReport;
	}, [fetchBreakReport]);

	// Intersection Observer for infinite scroll
	const lastItemRef = useCallback(node => {
		if (loading || loadingMore) return;
		if (observer.current) observer.current.disconnect();
		observer.current = new IntersectionObserver(entries => {
			if (entries[0].isIntersecting && hasMore && pagination) {
				const nextPage = currentPage + 1;
				if (nextPage <= pagination.last_page && fetchBreakReportRef.current) {
					setCurrentPage(nextPage);
					fetchBreakReportRef.current(nextPage, false);
				}
			}
		});
		if (node) observer.current.observe(node);
	}, [loading, loadingMore, hasMore, pagination, currentPage]);

	const handleDownloadCSV = async () => {
		if (!breakData || breakData.length === 0) {
			toast.warning('No data available to download');
			return;
		}
		if (!isReportRangeWithinMonths(filterParams, 3)) {
			toast.error('Please select a date range within 90 days to proceed.');
			return;
		}

		// Get user_id and token from localStorage
		const userId = localStorage.getItem('user_id');
		const token = localStorage.getItem('token');
		
		if (!userId) {
			toast.error('User ID not found');
			return;
		}

		if (!token) {
			toast.error('Authentication token not found');
			return;
		}

		// Create user-specific private channel name
		const channelName = `export-breaks.${userId}`;
		let channel;
		let handleEvent = null;
		let toastId = null;

		setDownloadLoading(true);
		
		// Show loading toast
		toastId = toast.loading("Downloading in progress...");
		
		try {
			// Join export channel — stopListening only (do not Echo.leave).
			channel = Echo.private(channelName);
			const eventName = 'export.status.updated';
			let handled = false;

			handleEvent = async (event) => {
				try {
					const status = event?.status || event?.data?.status;

					if (status === 'processing' || status === 'queued') {
						return;
					}

					if (handled) return;
					handled = true;

					if (status === 'failed' || status === 'error') {
						const errorMessage = event?.message || event?.data?.message || 'Export failed';
						toast.error(errorMessage);
						cleanup();
						setDownloadLoading(false);
						return;
					}

					const downloadUrl = event?.data?.download_url || 
									  event?.download_url ||
									  event?.data?.data?.download_url;

					if (!downloadUrl) {
						toast.error('Download URL not received. Please try again.');
						cleanup();
						setDownloadLoading(false);
						return;
					}

					try {
						await downloadFileWithAuth(
							downloadUrl,
							getDownloadFilename(event, downloadUrl, 'break-report.csv')
						);
						toast.success('Downloaded successfully');
					} catch (downloadError) {
						toast.error('Failed to start download');
					}

					cleanup();
					setDownloadLoading(false);
				} catch (error) {
					handled = true;
					toast.error('Failed to process download');
					cleanup();
					setDownloadLoading(false);
				}
			};

			const cleanup = () => {
				clearSharedUserChannelEvent(channelName, eventName, channel, handleEvent);
				if (toastId) {
					toast.dismiss(toastId);
					toastId = null;
				}
			};

			clearSharedUserChannelEvent(channelName, eventName, channel);

			try {
				channel.listen(`.${eventName}`, handleEvent);
			} catch (e) {}

			try {
				const pusherChannel = Echo?.connector?.pusher?.channel?.(`private-${channelName}`);
				pusherChannel?.bind?.(eventName, handleEvent);
			} catch (e) {}

			// Error handler
			if (typeof channel.error === 'function') {
				channel.error((error) => {
					toast.error('Socket connection error');
					cleanup();
					setDownloadLoading(false);
				});
			}

			// Wait for subscription to be ready before calling API
			await new Promise((resolve) => setTimeout(resolve, 500));

			// Calculate dates for export using utility
			const { startDate: downloadStartDateTime, endDate: downloadEndDateTime } = calculateReportDateRange(filterParams);

			// Call create API to trigger report generation
			const downloadParams = {
				daterange: `${downloadStartDateTime} / ${downloadEndDateTime}`,
				outlet: filterParams.outlet || selectedOutlet,
				user_id: userId
			};

			if (filterParams?.staff !== undefined) {
				if (Array.isArray(filterParams.staff)) {
					downloadParams.therapist = filterParams.staff.join(',');
				} else {
					downloadParams.therapist = String(filterParams.staff || '').trim();
				}
			}
			// Reason filter: prefer status (current), fallback to status_id (older)
			const selectedDownloadStatus = filterParams?.status ?? filterParams?.status_id;
			if (selectedDownloadStatus !== undefined) {
				if (Array.isArray(selectedDownloadStatus)) {
					downloadParams.status = selectedDownloadStatus.join(',');
				} else {
					downloadParams.status = String(selectedDownloadStatus || '').trim();
				}
			}
			if (filterParams.archive === 1) {
				downloadParams.archive = 1;
			}

			const createResponse = await createBreakReportDownload(downloadParams);

			// Check for success in the response
			const isSuccess = createResponse?.data?.data?.success || createResponse?.data?.success;
			
			if (isSuccess) {
				return; // Wait for socket event
			}

			// If not successful, handle error
			if (!isSuccess) {
				const backendError = createResponse?.data?.data?.message ||
								   createResponse?.data?.message || 
								   createResponse?.message || 
								   'Failed to initiate download';
				
				// Show user-friendly error message
				let userMessage = 'Failed to generate report. ';
				
				if (backendError.includes('S3') || backendError.includes('AWS') || backendError.includes('storage')) {
					userMessage += 'Server storage configuration issue. Please contact support.';
				} else if (backendError.includes('ACL')) {
					userMessage += 'File permission error. Please contact support.';
				} else {
					userMessage = backendError.substring(0, 200);
				}
				
				toast.error(userMessage, {
					autoClose: 6000
				});
				
				cleanup();
				setDownloadLoading(false);
				return;
			}

		} catch (error) {
			const errorMessage = error?.response?.data?.message || 
							   error?.message || 
							   'An error occurred while preparing download';
			
			toast.error(errorMessage, {
				autoClose: 5000
			});
			
			if (toastId) toast.dismiss(toastId);
			setDownloadLoading(false);
			
			try {
				clearSharedUserChannelEvent(channelName, 'export.status.updated', channel, handleEvent);
			} catch (e) {}
		}
	};

	return (
		<div className={styles.breakReportContainer}>
			{/* Header Section */}
		<ReportHeader
			outletName={outletName}
			periodText={periodText}
			onDownload={handleDownloadCSV}
			downloadLoading={downloadLoading}
			hasData={breakData && breakData.length > 0}
			downloadButtonText="Download CSV"
		/>

			{/* Table Section */}
			{loading ? (
				<div className={styles.loaderContainer}>
					<Loader />
				</div>
			) : breakData && breakData.length > 0 ? (
				<div className={styles.tableWrapper}>
					<table className={styles.breakTable}>
						<thead style={{ position: 'sticky', top: 0, zIndex: 10, }}>
							<tr>
								<th className={styles.colTherapistName}>Work Name</th>
								<th className={styles.colDate}>Date</th>
								<th className={styles.colTime}>Time</th>
								<th className={styles.colReason}>Reason</th>
								<th className={styles.colRemark}>Remark</th>
							</tr>
						</thead>
						<tbody>
							{breakData.map((item, index) => {
								const isLastItem = index === breakData.length - 1;
								
								return (
									<tr 
										key={item.id + '_' + index}
										ref={isLastItem ? lastItemRef : null}
									>
										<td className={styles.colTherapistName}>
											{item.user_name || 'N/A'}
										</td>
										<td className={styles.colDate}>
											{item.date || 'N/A'}
										</td>
									<td className={styles.colTime}>
										{item.time || '-'}
									</td>
									<td className={styles.colReason}>
										{item.status_name || '-'}
									</td>
									<td className={styles.colRemark}>
										{item.remark || '-'}
									</td>
								</tr>
							);
						})}
						</tbody>
					</table>
				</div>
			) : (
				<div className={styles.noData}>
					<Placeholder
						icon={<SearchNotFoundIcon width="24" height="24" color="white"/>}
						heading="No break data found"
						para="Please try changing the filters"
					/>
				</div>
			)}
		</div>
	);
};

export default BreakReport;

